import { Renderer2 } from '@angular/core';
import { Constructor } from './constructor';

export interface BreakpointValue {
  default?: string;
  xs?: string;
  sm?: string;
  md?: string;
  lg?: string;
  xl?: string;
}

export type InputValue = BreakpointValue | string | undefined;

export interface CanGenerateBreakpointClasses {
  renderer: Renderer2;
}

export interface BreakpointClasses {
  isBreakpointInputValue: (value: InputValue) => boolean;
  addBreakpointClasses: (
    element: HTMLElement,
    classPrefix: string,
    value: InputValue
  ) => void;
  removeBreakpointClasses: (
    element: HTMLElement,
    classPrefix: string,
    value: InputValue
  ) => void;
}

export type BreakpointClassesCtor = Constructor<BreakpointClasses>;

/**
 * Mixin to augment a component / directive with the ability to add and remove
 * breakpoint modifier CSS classes
 */
export function mixinBreakpointClassesGenerator<
  T extends Constructor<CanGenerateBreakpointClasses>
>(base: T): BreakpointClassesCtor & T {
  return class extends base {
    private breakpointsList = ['xs', 'sm', 'md', 'lg', 'xl', 'default'];

    constructor(...args: any[]) {
      super(...args);
    }

    /**
     * Checks if provided value is an `InputValue` like value
     */
    isBreakpointInputValue(value: InputValue) {
      return typeof value === 'string' || typeof value === 'object'
        ? true
        : false;
    }

    /**
     * Adds breakpoint classes to a HTMLElement with a prefix
     */
    addBreakpointClasses(
      element: HTMLElement,
      classPrefix: string,
      value: InputValue
    ) {
      if (!this.isBreakpointInputValue(value)) {
        return;
      }

      if (typeof value === 'string') {
        return this.renderer.addClass(element, `${classPrefix}-${value}`);
      }

      const breakpoints = value as BreakpointValue;

      Object.keys(breakpoints).forEach((breakpoint) => {
        if (this.breakpointsList.includes(breakpoint)) {
          this.renderer.addClass(
            element,
            `${classPrefix}-${breakpoints[breakpoint]}${
              breakpoint !== 'default' ? '-' + breakpoint : ''
            }`
          );
        }
      });
    }

    /**
     * Removes breakpoint classes from a HTMLElement with a prefix
     */
    removeBreakpointClasses(
      element: HTMLElement,
      classPrefix: string,
      value: InputValue
    ) {
      if (!this.isBreakpointInputValue(value)) {
        return;
      }

      if (typeof value === 'string') {
        return this.renderer.removeClass(element, `${classPrefix}-${value}`);
      }

      const breakpoints = value as BreakpointValue;

      Object.keys(breakpoints).forEach((breakpoint) => {
        if (this.breakpointsList.includes(breakpoint)) {
          this.renderer.removeClass(
            element,
            `${classPrefix}-${breakpoints[breakpoint]}${
              breakpoint !== 'default' ? '-' + breakpoint : ''
            }`
          );
        }
      });
    }
  };
}
